// src/pages/admin/AdminMessages.tsx
// Route: /admin/messages
//
// Two-panel inbox: left = thread list, right = open thread + reply box.
// Identical UX mental model to UserMessages but from the admin side.

import { useState, useEffect, useRef, useMemo } from "react";
import {
  useAdminGetMessagesQuery,
  useAdminGetThreadQuery,
  useAdminReplyToThreadMutation,
  useAdminMarkThreadReadMutation,
  useAdminSearchMessagesQuery,
} from "../../app/api/adminMessagesApi";
import type {
  AdminThreadPreview,
  AdminThreadMessage,
  ThreadParent,
} from "../../app/api/adminMessagesApi";
import { PageHeader, Skeleton, Avatar } from "./AdminShared";

import { Check, CheckCheck, MessagesSquare } from "lucide-react";
/* ─── helpers ────────────────────────────────────────────────────── */

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateSeparator(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/* ─── thread list item ───────────────────────────────────────────── */

function ThreadItem({
  t,
  active,
  onClick,
}: {
  t: AdminThreadPreview;
  active: boolean;
  onClick: () => void;
}) {
  const hasUnread = t.unreadFromParent > 0;

  return (
    <button
      onClick={onClick}
      className={[
        "w-full flex items-start gap-3 rounded-[16px] px-3 py-3 text-left transition-all",
        active ? "bg-[#ebeff7]" : "hover:bg-[#f9fafc]",
      ].join(" ")}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <Avatar
          firstName={t.parentName.split(" ")[0]}
          lastName={t.parentName.split(" ")[1] ?? ""}
          size="sm"
        />
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-[#efb0a4] text-[7px] font-bold text-white">
            {t.unreadFromParent > 9 ? "9+" : t.unreadFromParent}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        {/* Name + time */}
        <div className="flex items-center justify-between gap-2">
          <p
            className={`text-xs truncate ${hasUnread ? "font-bold text-[#16213e]" : "font-semibold text-[#3b4560]"}`}
          >
            {t.parentName}
          </p>
          <span className="flex-shrink-0 text-[11.5px] text-[#a6adbf]">
            {formatRelative(t.lastMessageAt)}
          </span>
        </div>
        {/* Subject */}
        <p className="text-[12.5px] text-[#5a6278] truncate mt-0.5">
          {t.subject}
        </p>
        {/* Preview */}
        <p
          className={`text-[12.5px] truncate mt-0.5 ${hasUnread ? "text-[#3b4560] font-semibold" : "text-[#8189a0]"}`}
        >
          {t.lastDirection === "outbound" && (
            <span className="text-[#1b2d5e]">You: </span>
          )}
          {t.lastMessage}
        </p>
      </div>
    </button>
  );
}

/* ─── thread view ─────────────────────────────────────────────────── */

function ThreadView({
  threadId,
  onBack,
}: {
  threadId: string;
  onBack: () => void;
}) {
  const { data, isLoading } = useAdminGetThreadQuery(threadId);
  const [reply, { isLoading: sending }] = useAdminReplyToThreadMutation();
  const [markRead] = useAdminMarkThreadReadMutation();

  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const messages: AdminThreadMessage[] = data?.data ?? [];
  const parent: ThreadParent | null = data?.parent ?? null;

  // Scroll to bottom when messages load
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Mark thread read when opened
  useEffect(() => {
    markRead(threadId).catch(() => {});
  }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group by date for separators
  const grouped = useMemo(
    () =>
      messages.reduce(
        (acc: { date: string; msgs: AdminThreadMessage[] }[], msg) => {
          const key = new Date(msg.createdAt).toDateString();
          const last = acc[acc.length - 1];
          if (last && last.date === key) last.msgs.push(msg);
          else acc.push({ date: key, msgs: [msg] });
          return acc;
        },
        [],
      ),
    [messages],
  );

  const handleSend = async () => {
    if (!body.trim()) return;
    setError("");
    try {
      await reply({ threadId, body: body.trim() }).unwrap();
      setBody("");
      textareaRef.current?.focus();
    } catch (e: any) {
      setError(e?.data?.error ?? "Failed to send. Please try again.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const subject = messages[0]?.subject ?? "Thread";

  return (
    <div className="flex h-full flex-col">
      {/* Thread header */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#ebeff7] px-5 py-4">
        {/* Back on mobile */}
        <button
          onClick={onBack}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8] transition lg:hidden"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 4L6 8l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Parent info */}
        {parent && (
          <Avatar
            firstName={parent.firstName}
            lastName={parent.lastName}
            size="md"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#16213e] truncate">{subject}</p>
          {parent && (
            <p className="text-[12.5px] text-[#8189a0]">
              {parent.name} ·{" "}
              <span className="text-[#a6adbf]">{parent.email}</span>
            </p>
          )}
        </div>
        <span className="flex-shrink-0 rounded-full bg-[#f1f3f8] px-2.5 py-0.5 text-[11.5px] font-bold text-[#8189a0]">
          {messages.length} message{messages.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-[18px]" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-[#8189a0] py-8">
            No messages in this thread.
          </p>
        ) : (
          grouped.map((group: { date: string; msgs: AdminThreadMessage[] }) => (
            <div key={group.date}>
              {/* Date separator */}
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px bg-[#ebeff7]" />
                <span className="text-[11.5px] font-bold text-[#c9cfe0]">
                  {formatDateSeparator(group.msgs[0].createdAt)}
                </span>
                <div className="flex-1 h-px bg-[#ebeff7]" />
              </div>

              <div className="space-y-3">
                {group.msgs.map((msg: AdminThreadMessage) => {
                  const isAdmin = msg.direction === "outbound"; // admin sent it
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}
                    >
                      {/* Parent avatar on left */}
                      {!isAdmin && parent && (
                        <Avatar
                          firstName={parent.firstName}
                          lastName={parent.lastName}
                          size="sm"
                          className="mr-2 mt-1 flex-shrink-0"
                        />
                      )}

                      <div
                        className={[
                          "max-w-[78%] rounded-[18px] px-4 py-3",
                          isAdmin
                            ? "bg-[#1b2d5e] text-white rounded-br-[6px]"
                            : "bg-white border border-[#e5e8ef] text-[#16213e] rounded-bl-[6px]",
                        ].join(" ")}
                      >
                        <p
                          className={`text-[11.5px] font-bold mb-1 ${isAdmin ? "text-white/70" : "text-[#1b2d5e]"}`}
                        >
                          {isAdmin
                            ? msg.senderName
                            : (parent?.name ?? msg.senderName)}
                        </p>
                        <p
                          className={`text-sm leading-6 whitespace-pre-wrap ${isAdmin ? "text-white" : "text-[#16213e]"}`}
                        >
                          {msg.body}
                        </p>
                        <p
                          className={`text-[11.5px] mt-1.5 ${isAdmin ? "text-white/50 text-right" : "text-[#a6adbf]"}`}
                        >
                          {formatTime(msg.createdAt)}
                          {isAdmin && (
                            <span className="ml-1">
                              {msg.readByParent ? <CheckCheck className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> : <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Admin avatar on right */}
                      {isAdmin && (
                        <div className="ml-2 mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[8px] bg-[#ebeff7] text-[11px] font-bold text-[#1b2d5e]">
                          FSL
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply box */}
      <div className="flex-shrink-0 border-t border-[#ebeff7] px-5 py-4 space-y-2">
        {error && (
          <p className="rounded-xl border border-[#f5c0c0] bg-[#fff3f3] px-3 py-1.5 text-xs text-[#9b2a2a]">
            {error}
          </p>
        )}
        <div className="flex items-end gap-3">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            placeholder="Type your reply… (⌘+Enter to send)"
            className="flex-1 resize-none rounded-[16px] border border-[#dde2ec] px-4 py-3 text-sm outline-none focus:border-[#1b2d5e] focus:ring-1 focus:ring-[#1b2d5e]/20 transition"
          />
          <button
            onClick={handleSend}
            disabled={sending || !body.trim()}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[14px] bg-[#1b2d5e] text-white shadow-[0_4px_12px_rgba(27,45,94,0.35)] transition hover:bg-[#24397a] disabled:opacity-40 disabled:cursor-not-allowed"
            title="Send reply (⌘+Enter)"
          >
            {sending ? (
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 16 16"
                fill="none"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeDasharray="30"
                  strokeDashoffset="10"
                />
              </svg>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                <path d="M2 8l12-6-6 12V8H2z" fill="currentColor" />
              </svg>
            )}
          </button>
        </div>
        <p className="text-[11.5px] text-[#a6adbf]">
          Your reply will be delivered to {parent?.name ?? "the parent"}'s inbox
          immediately.
        </p>
      </div>
    </div>
  );
}

/* ─── empty state ─────────────────────────────────────────────────── */

function EmptyPanel() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-6">
      <span className="opacity-20"><MessagesSquare className="h-12 w-12" aria-hidden /></span>
      <p className="text-sm font-semibold text-[#8189a0]">
        Select a conversation
      </p>
      <p className="text-xs text-[#a6adbf]">
        Click any thread on the left to read and reply.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */

export default function AdminMessages() {
  const [statusFilter, setStatusFilter] = useState<"all" | "unread">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [activeThread, setActiveThread] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data: inboxData, isLoading: inboxLoading } = useAdminGetMessagesQuery(
    { status: statusFilter, limit: 50 },
    { skip: !!debouncedQ, pollingInterval: 30000 }, // re-fetch every 30s
  );

  const { data: searchData, isLoading: searchLoading } =
    useAdminSearchMessagesQuery(debouncedQ, { skip: !debouncedQ });

  const isLoading = debouncedQ ? searchLoading : inboxLoading;
  const threads: AdminThreadPreview[] = debouncedQ
    ? (searchData?.data ?? [])
    : (inboxData?.data ?? []);

  const totalUnread =
    inboxData?.data.reduce((s, t) => s + t.unreadFromParent, 0) ?? 0;

  // Auto-select first thread on desktop
  useEffect(() => {
    if (!activeThread && threads.length > 0) {
      setActiveThread(threads[0].threadId);
    }
  }, [threads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <PageHeader
        title="Messages"
        subtitle="Respond to parent enquiries. Replies are delivered to the parent portal instantly."
      />

      <div className="flex h-[calc(100vh-13rem)] overflow-hidden rounded-[22px] border border-[#e5e8ef] bg-white shadow-[0_2px_12px_rgba(27,45,94,0.06)]">
        {/* ── Left panel: thread list ── */}
        <div
          className={[
            "flex flex-col border-r border-[#ebeff7] flex-shrink-0",
            activeThread
              ? "hidden lg:flex lg:w-[320px] xl:w-[360px]"
              : "flex w-full lg:w-[320px] xl:w-[360px]",
          ].join(" ")}
        >
          {/* List header */}
          <div className="border-b border-[#ebeff7] px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-[#16213e]">Inbox</h2>
                {totalUnread > 0 && (
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#efb0a4] px-1.5 text-[11.5px] font-bold text-[#5b2e28]">
                    {totalUnread}
                  </span>
                )}
              </div>
              {/* Filter tabs */}
              <div className="flex rounded-xl border border-[#dde2ec] overflow-hidden">
                {(["all", "unread"] as const).map((f: "all" | "unread") => (
                  <button
                    key={f}
                    onClick={() => {
                      setStatusFilter(f);
                      setSearchQuery("");
                    }}
                    className={[
                      "px-3 py-1 text-[11.5px] font-bold transition",
                      statusFilter === f && !debouncedQ
                        ? "bg-[#1b2d5e] text-white"
                        : "text-[#8189a0] hover:bg-[#f1f3f8]",
                    ].join(" ")}
                  >
                    {f === "all" ? "All" : "Unread"}
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a6adbf]"
                viewBox="0 0 16 16"
                fill="none"
              >
                <circle
                  cx="7"
                  cy="7"
                  r="4.5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                />
                <path
                  d="M10.5 10.5l3 3"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search parent or subject…"
                className="w-full rounded-xl border border-[#dde2ec] bg-[#f9fafc] py-2 pl-8 pr-3 text-xs outline-none focus:border-[#1b2d5e] transition"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8189a0] hover:text-[#16213e]"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {isLoading ? (
              <div className="space-y-2 p-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 rounded-[16px]" />
                ))}
              </div>
            ) : threads.length === 0 ? (
              <div className="flex flex-col items-center py-12 px-4 text-center">
                <span className="mb-2"><MessagesSquare className="h-8 w-8" aria-hidden /></span>
                <p className="text-sm font-semibold text-[#16213e]">
                  {debouncedQ ? "No results found" : "No messages yet"}
                </p>
                <p className="text-xs text-[#8189a0] mt-1">
                  {debouncedQ
                    ? "Try a different search term."
                    : "Messages from parents will appear here."}
                </p>
              </div>
            ) : (
              threads.map((t: AdminThreadPreview) => (
                <ThreadItem
                  key={t.threadId}
                  t={t}
                  active={t.threadId === activeThread}
                  onClick={() => setActiveThread(t.threadId)}
                />
              ))
            )}
          </div>

          {/* Footer stat */}
          {!isLoading && threads.length > 0 && (
            <div className="border-t border-[#ebeff7] px-4 py-2.5">
              <p className="text-[11.5px] text-[#a6adbf]">
                {threads.length} thread{threads.length !== 1 ? "s" : ""}
                {totalUnread > 0 && ` · ${totalUnread} unread`}
              </p>
            </div>
          )}
        </div>

        {/* ── Right panel: open thread ── */}
        <div
          className={[
            "flex-1 min-w-0",
            activeThread ? "flex flex-col" : "hidden lg:flex lg:flex-col",
          ].join(" ")}
        >
          {activeThread ? (
            <ThreadView
              key={activeThread}
              threadId={activeThread}
              onBack={() => setActiveThread(null)}
            />
          ) : (
            <EmptyPanel />
          )}
        </div>
      </div>
    </>
  );
}
